import React, { Suspense, lazy } from 'react';
import "@/App.css";
import { Routes, Route } from "react-router-dom";
// Direto do sonner, e não de components/ui/sonner.jsx: aquele wrapper
// chama useTheme() do next-themes, e não existe ThemeProvider montado.
import { Toaster } from "sonner";
import { Header } from "./components/Header";
import { Hero } from "./components/Hero";
import { TrustStats } from "./components/TrustStats";
import { ProblemTypes } from "./components/ProblemTypes";
import { HowItWorks } from "./components/HowItWorks";
import { CTABanner } from "./components/CTABanner";
import { Services } from "./components/Services";
import { FAQ } from "./components/FAQ";
import { CTASection } from "./components/CTASection";
import { Footer } from "./components/Footer";

// Lazy Loaded Routes
const ElegibilidadeVoosPage = lazy(() => import("./components/elegibilidade-voos/ElegibilidadeVoosPage").then(module => ({ default: module.ElegibilidadeVoosPage })));
const TerminalPage = lazy(() => import("./components/elegibilidade-voos/TerminalPage").then(module => ({ default: module.TerminalPage })));
const DadosPessoaisPage = lazy(() => import("./components/elegibilidade-voos/DadosPessoaisPage").then(module => ({ default: module.DadosPessoaisPage })));
const PassageirosPage = lazy(() => import("./components/elegibilidade-voos/PassageirosPage").then(module => ({ default: module.PassageirosPage })));
const DetalhesPedidoPage = lazy(() => import("./components/elegibilidade-voos/DetalhesPedidoPage").then(module => ({ default: module.DetalhesPedidoPage })));
const RecebeuSuportePage = lazy(() => import("./components/elegibilidade-voos/RecebeuSuportePage").then(module => ({ default: module.RecebeuSuportePage })));
const EnderecoPage = lazy(() => import("./components/elegibilidade-voos/EnderecoPage").then(module => ({ default: module.EnderecoPage })));
const CpfPage = lazy(() => import("./components/elegibilidade-voos/CpfPage").then(module => ({ default: module.CpfPage })));
const ProvasPage = lazy(() => import("./components/elegibilidade-voos/ProvasPage").then(module => ({ default: module.ProvasPage })));
const PassageirosPageCompleto = lazy(() => import("./components/elegibilidade-voos/PassageirosPageCompleto").then(module => ({ default: module.PassageirosPageCompleto })));
const ComprovanteEnderecoPage = lazy(() => import("./components/elegibilidade-voos/ComprovanteEnderecoPage").then(module => ({ default: module.ComprovanteEnderecoPage })));
const AssinaturaPage = lazy(() => import("./components/elegibilidade-voos/AssinaturaPage").then(module => ({ default: module.AssinaturaPage })));
const ConclusaoPage = lazy(() => import("./components/elegibilidade-voos/ConclusaoPage").then(module => ({ default: module.ConclusaoPage })));
const PoliticaPrivacidadePage = lazy(() => import("./components/PoliticaPrivacidadePage").then(module => ({ default: module.PoliticaPrivacidadePage })));
const NotFoundPage = lazy(() => import("./components/NotFoundPage").then(module => ({ default: module.NotFoundPage })));
const LoginPage = lazy(() => import("./components/auth/LoginPage").then(module => ({ default: module.LoginPage })));
const PainelHomePage = lazy(() => import("./components/cliente/PainelHomePage").then(module => ({ default: module.PainelHomePage })));
const AdminLoginPage = lazy(() => import("./components/admin/AdminLoginPage").then(module => ({ default: module.AdminLoginPage })));
const AdminGuard = lazy(() => import("./components/admin/AdminGuard").then(module => ({ default: module.AdminGuard })));
const AdminDashboardPage = lazy(() => import("./components/admin/AdminDashboardPage").then(module => ({ default: module.AdminDashboardPage })));
const AdminLeadsPage = lazy(() => import("./components/admin/AdminLeadsPage").then(module => ({ default: module.AdminLeadsPage })));
const AdminClienteDetalhePage = lazy(() => import("./components/admin/AdminClienteDetalhePage").then(module => ({ default: module.AdminClienteDetalhePage })));

const SuspenseFallback = () => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center">
    <div className="w-8 h-8 rounded-full border-4 border-slate-200 border-t-blue-600 animate-spin"></div>
  </div>
);

const HomePage = () => (
  <div className="App min-h-screen bg-slate-50">
    <Header />
    <main>
      <Hero />
      <HowItWorks />
      <TrustStats />
      <ProblemTypes />
      <CTABanner />
      <Services />
      <FAQ />
      <CTASection />
    </main>
    <Footer />
  </div>
);

function App() {
  return (
    <Suspense fallback={<SuspenseFallback />}>
      <Toaster
        position="top-center"
        richColors
        closeButton
        toastOptions={{ style: { fontWeight: 500 } }}
      />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/painel/home" element={<PainelHomePage />} />
        <Route path="/elegibilidade/voos" element={<ElegibilidadeVoosPage />} />
        <Route path="/elegibilidade/voo/elegivel" element={<ElegibilidadeVoosPage />} />
        <Route path="/elegibilidade/voo/:stepSlug" element={<ElegibilidadeVoosPage />} />
        <Route path="/elegibilidade/bagagem/:stepSlug" element={<ElegibilidadeVoosPage />} />
        <Route path="/elegibilidade/voo/inelegivel/:terminalSlug" element={<TerminalPage />} />
        <Route path="/elegibilidade/dados-pessoais" element={<DadosPessoaisPage />} />
        <Route path="/elegibilidade/passageiros" element={<PassageirosPage />} />
        <Route path="/elegibilidade/detalhes-pedido" element={<DetalhesPedidoPage />} />
        <Route path="/elegibilidade/recebeu-suporte" element={<RecebeuSuportePage />} />
        <Route path="/elegibilidade/concluido" element={<ConclusaoPage modo="curto" />} />

        {/* Fluxo completo (documentos + assinatura) — preservado, não encadeado
            a partir do wizard. Pressupõe pedido_id já criado (dados-pessoais
            roda igual nos dois fluxos). Reativar: linkar pra cá de novo. */}
        <Route path="/elegibilidade-completo/provas" element={<ProvasPage />} />
        <Route path="/elegibilidade-completo/passageiros" element={<PassageirosPageCompleto />} />
        <Route path="/elegibilidade-completo/endereco" element={<EnderecoPage />} />
        <Route path="/elegibilidade-completo/comprovante-endereco" element={<ComprovanteEnderecoPage />} />
        <Route path="/elegibilidade-completo/documentos-pessoais" element={<CpfPage />} />
        <Route path="/elegibilidade-completo/assinatura" element={<AssinaturaPage />} />
        <Route path="/elegibilidade-completo/concluido" element={<ConclusaoPage modo="completo" />} />

        <Route path="/politica-de-privacidade" element={<PoliticaPrivacidadePage />} />

        {/* Admin Routes */}
        <Route path="/admin/login" element={<AdminLoginPage />} />
        <Route path="/admin/dashboard" element={<AdminGuard><AdminDashboardPage /></AdminGuard>} />
        <Route path="/admin/leads" element={<AdminGuard><AdminLeadsPage /></AdminGuard>} />
        <Route path="/admin/lead/:id" element={<AdminGuard><AdminClienteDetalhePage /></AdminGuard>} />

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default App;
